import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { readEffectiveInventorizationIdentity } from '@/features/inventorization/domain/effective-inventorization-values';
import type { LocationBootstrapMeter } from '@/features/location-bootstrap/location-bootstrap-types';
import {
  findBackendMeterMatches,
  findOtherLocationResults,
  mergeMeterResults,
} from '@/features/scan/scan-meter-search-results';
import type { CaptureSession } from '@/shared/capture/capture-session';
import type { CaptureSessionRepository } from '@/shared/db/repositories/capture-session-repository';
import type { MeterSearchResult, NormalizedMeterIdentifier } from '@/shared/meter-identification';
import { normalizeMeterIdentifier, normalizeSearchField } from '@/shared/meter-identification';
import { messages } from './messages';

export { findBackendMeterMatches } from '@/features/scan/scan-meter-search-results';

interface ManualMeterSearchState {
  identifier: NormalizedMeterIdentifier | null;
  otherLocationResults: MeterSearchResult[];
  results: MeterSearchResult[];
  queuedMatches: CaptureSession[];
  isSearching: boolean;
  error: string | null;
  hasSearched: boolean;
}

const initialManualMeterSearchState: ManualMeterSearchState = {
  identifier: null,
  otherLocationResults: [],
  queuedMatches: [],
  results: [],
  isSearching: false,
  error: null,
  hasSearched: false,
};

export function useManualMeterSearch(
  input: { backendMeters?: LocationBootstrapMeter[]; locationId?: number | string } = {},
) {
  const intl = useIntl();
  const services = useInventoryAppServices();
  const repositories = useMemo(
    () => ({
      captureSessions: services.captureSessions,
      meters: services.meterSearch,
    }),
    [services.captureSessions, services.meterSearch],
  );
  const [state, setState] = useState<ManualMeterSearchState>(initialManualMeterSearchState);

  const search = useCallback(
    async (rawValue: string) => {
      const identifier = normalizeMeterIdentifier(rawValue);
      setState(current => ({ ...current, identifier, isSearching: true, error: null, hasSearched: true }));

      if (!identifier.normalizedValue) {
        setState({
          identifier,
          otherLocationResults: [],
          queuedMatches: [],
          results: [],
          isSearching: false,
          error: intl.formatMessage(messages.scanInvalidSearchValue),
          hasSearched: true,
        });
        return undefined;
      }

      try {
        const [localResults, allCachedResults, queuedMatches] = await Promise.all([
          repositories.meters.findByIdentifier(identifier, { locationId: input.locationId }),
          repositories.meters.findByIdentifier(identifier),
          findLocalSessionMatches(repositories.captureSessions, identifier),
        ]);
        const mergedResults = mergeMeterResults([
          ...findBackendMeterMatches(input.backendMeters ?? [], identifier),
          ...localResults,
        ]);
        const otherLocationResults = findOtherLocationResults(allCachedResults, mergedResults, input.locationId);
        setState({
          identifier,
          otherLocationResults,
          queuedMatches,
          results: mergedResults,
          isSearching: false,
          error: null,
          hasSearched: true,
        });

        return { identifier, otherLocationResults, queuedMatches, results: mergedResults };
      } catch (error) {
        setState({
          identifier,
          otherLocationResults: [],
          queuedMatches: [],
          results: [],
          isSearching: false,
          error: error instanceof Error ? error.message : intl.formatMessage(messages.scanErrorSearchLocalCache),
          hasSearched: true,
        });

        return undefined;
      }
    },
    [input.backendMeters, input.locationId, intl, repositories],
  );

  const reset = useCallback(() => setState(initialManualMeterSearchState), []);

  return {
    ...state,
    reset,
    search,
  };
}

export async function findLocalSessionMatches(
  repository: CaptureSessionRepository,
  identifier: NormalizedMeterIdentifier,
) {
  const sessions = await repository.listInventorizationSessions();

  return sessions.filter(session => {
    const identity = readEffectiveInventorizationIdentity(session);

    return (
      normalizeSearchField(identity.apiKey) === identifier.normalizedValue ||
      normalizeSearchField(identity.serial) === identifier.normalizedValue
    );
  });
}
